import { useTranslation } from 'react-i18next';
import kitBlueSm from '@/assets/kits/kit-blue-sm.svg';
import kitWhiteSm from '@/assets/kits/kit-white-sm.svg';
import { formatEventKickoff } from '@/domain/event-details';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { Badge } from '../common/badge';
import { Icon } from '../common/icon';

export interface EventMatchHeaderProps {
  home: string;
  away: string;
  type?: 'prematch' | 'live';
  /** Prematch kick-off label; preferred over `date` when already localized. */
  kickoff?: string;
  /** Raw API date — formatted with the active language when provided. */
  date?: string | null;
  clock?: string;
  homeScore?: string;
  awayScore?: string;
  onShowStats?: () => void;
  className?: string;
}

const KIT_SRC = [kitWhiteSm, kitBlueSm] as const;

/** Match summary card from the Figma Event Detail frame. */
export function EventMatchHeader({
  home,
  away,
  type = 'prematch',
  kickoff,
  date,
  clock,
  homeScore,
  awayScore,
  onShowStats,
  className,
}: EventMatchHeaderProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const isLive = type === 'live';
  const kickoffLabel =
    !isLive && date
      ? formatEventKickoff(date, selectedLanguage || i18n.language) ?? kickoff
      : kickoff;
  const pill = isLive ? clock : kickoffLabel;
  const showScores = isLive && (homeScore !== undefined || awayScore !== undefined);

  return (
    <section
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg bg-bg-surface px-3 pt-3 pb-4',
        className,
      )}
    >
      {pill || isLive ? (
        <div className="flex items-center justify-center rounded-full bg-bg-elevated px-3 py-1.5">
          {isLive ? (
            <span className="flex items-center gap-2">
              <Badge tone="live">{t('Live', { defaultValue: 'Live' })}</Badge>
              {clock ? <span className="text-caption text-text-secondary">{clock}</span> : null}
            </span>
          ) : (
            <p className="text-caption whitespace-nowrap text-text-secondary">{kickoffLabel}</p>
          )}
        </div>
      ) : null}

      <div className="flex w-full items-center justify-center gap-2">
        <Competitor name={home} kitSrc={KIT_SRC[0]} />
        <div className="flex shrink-0 flex-col items-center justify-center gap-1">
          {showScores ? (
            <p className="text-heading-s font-bold tabular-nums text-text-primary">
              {homeScore ?? '0'} – {awayScore ?? '0'}
            </p>
          ) : null}
          {onShowStats ? (
            <button
              type="button"
              onClick={onShowStats}
              aria-label={t('MatchStatistics', { defaultValue: 'Match statistics' })}
              className={cn(
                'flex size-10 shrink-0 items-center justify-center rounded-md border border-border-default',
                'bg-bg-surface text-icon-muted hover:bg-bg-elevated hover:text-icon-default',
              )}
            >
              <Icon name="stats" size={18} />
            </button>
          ) : null}
        </div>
        <Competitor name={away} kitSrc={KIT_SRC[1]} />
      </div>
    </section>
  );
}

function Competitor({ name, kitSrc }: { name: string; kitSrc: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-2">
      <span className="relative size-8 shrink-0 overflow-clip">
        <img src={kitSrc} alt="" className="size-full object-contain" />
      </span>
      <p className="w-full text-center text-caption font-bold text-text-primary">{name}</p>
    </div>
  );
}
